/**
 * @fileoverview FlawChecklist Component - Clean, Compact Design
 * @module FlawChecklist
 * 
 * Interactive checklist for each flaw type in Step 3.
 * Redesigned for better scannability with inline follow-up fields.
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Check, Save, Loader2 } from 'lucide-react';
import FollowUpFields from '../common/FollowUpFields';
import { FLAW_FOLLOW_UP_CONFIGS } from '../common/followUpConfigs';

export interface ChecklistItem {
  key: string;
  label: string;
}

export interface FlawChecklistProps {
  flawType: string;
  items: ChecklistItem[];
  initialData?: Record<string, boolean>;
  initialFollowUpData?: Record<string, Record<string, string | number>>;
  initialNotes?: string;
  onSave: (data: { 
    checklistData: Record<string, boolean>; 
    followUpData: Record<string, Record<string, string | number>>;
    notes: string;
  }) => Promise<void>;
  isSaving?: boolean;
}

const FlawChecklist: React.FC<FlawChecklistProps> = ({
  flawType,
  items,
  initialData = {},
  initialFollowUpData = {},
  initialNotes = '',
  onSave,
  isSaving = false
}) => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(initialData);
  const [followUpData, setFollowUpData] = useState<Record<string, Record<string, string | number>>>(initialFollowUpData);
  const [notes, setNotes] = useState<string>(initialNotes);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setCheckedItems(initialData);
    setFollowUpData(initialFollowUpData);
    setNotes(initialNotes);
    setHasChanges(false);
  }, [initialData, initialFollowUpData, initialNotes, flawType]);

  const handleToggle = (key: string) => {
    const newChecked = { ...checkedItems, [key]: !checkedItems[key] };
    setCheckedItems(newChecked);
    setHasChanges(true);
    
    if (checkedItems[key] && followUpData[key]) {
      const newFollowUpData = { ...followUpData };
      delete newFollowUpData[key];
      setFollowUpData(newFollowUpData);
    }
  };

  const handleFollowUpChange = (itemKey: string, fieldName: string, value: string | number) => {
    setFollowUpData(prev => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || {}),
        [fieldName]: value
      }
    }));
    setHasChanges(true);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validate that at least one item is checked or notes are provided
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    if (checkedCount === 0 && !notes.trim()) {
      toast.error('Please check at least one item or add notes before saving.');
      return;
    }

    // Clean followUpData: strip empty string values so optional fields don't cause validation errors
    const cleanedFollowUpData: Record<string, Record<string, string | number>> = {};
    for (const [questionId, fields] of Object.entries(followUpData)) {
      const cleanedFields: Record<string, string | number> = {};
      for (const [key, val] of Object.entries(fields)) {
        if (val !== '' && val !== undefined && val !== null) {
          cleanedFields[key] = val;
        }
      }
      if (Object.keys(cleanedFields).length > 0) {
        cleanedFollowUpData[questionId] = cleanedFields;
      }
    }

    await onSave({ checklistData: checkedItems, followUpData: cleanedFollowUpData, notes });
    setHasChanges(false);
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="space-y-3">
      {/* Compact Header with Counter */}
      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
        <p className="text-xs text-gray-500">
          Check issues found in your county evidence
        </p>
        {checkedCount > 0 && (
          <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
            {checkedCount} selected
          </span>
        )}
      </div>

      {/* Tight Checklist Grid */}
      <div className="space-y-1">
        {items.map((item) => {
          const isChecked = checkedItems[item.key] || false;
          const followUpConfig = FLAW_FOLLOW_UP_CONFIGS[item.key];
          const hasFollowUp = !!followUpConfig;
          
          return (
            <div key={item.key} className="group">
              <button
                onClick={() => handleToggle(item.key)}
                className={`w-full flex items-start gap-2.5 px-3 py-2 rounded-lg text-left transition-all ${
                  isChecked 
                    ? 'bg-teal-50 ring-1 ring-teal-200' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-4 h-4 mt-0.5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                  isChecked 
                    ? 'bg-teal-500 text-white' 
                    : 'border border-gray-300 bg-white group-hover:border-gray-400'
                }`}>
                  {isChecked && <Check className="w-3 h-3" strokeWidth={3} />}
                </div>
                <span className={`text-sm leading-tight ${
                  isChecked ? 'text-gray-900 font-medium' : 'text-gray-600'
                }`}>
                  {item.label}
                </span>
              </button>
              
              {/* Inline Follow-up Fields */}
              {hasFollowUp && (
                <FollowUpFields
                  config={followUpConfig}
                  values={followUpData[item.key] || {}}
                  onChange={(fieldName, value) => handleFollowUpChange(item.key, fieldName, value)}
                  isVisible={isChecked}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Compact Notes */}
      <div className="pt-3 border-t border-gray-100">
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Notes <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={handleNotesChange}
          placeholder="Additional details..."
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 resize-none"
        />
      </div>

      {/* Sticky Save Bar */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {hasChanges && <span className="text-slate-500">• Unsaved changes</span>}
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            hasChanges && !isSaving
              ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5" />
              Save
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FlawChecklist;
